type SvgDimensions = {
  width: number;
  height: number;
};

const fallbackDimensions: SvgDimensions = { width: 1200, height: 630 };
const maxSharePixels = 4_000_000;

function readDimensions(svg: string): SvgDimensions {
  const openingTag = svg.match(/<svg\b[^>]*>/i)?.[0] ?? "";
  const readAttribute = (name: string) => {
    const value = openingTag.match(new RegExp(`${name}=["']([0-9.]+)["']`, "i"))?.[1];
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };
  const width = readAttribute("width") ?? fallbackDimensions.width;
  const height = readAttribute("height") ?? fallbackDimensions.height;
  return { width, height };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The replay card could not be prepared for sharing."));
    image.src = url;
  });
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("The replay card could not be prepared for sharing."));
    }, "image/png");
  });
}

/** Convert the same SVG used by the replay card into a portable PNG file. */
export async function replayCardPngFile(svg: string, filename: string): Promise<File> {
  const dimensions = readDimensions(svg);
  const scale = Math.min(2, Math.sqrt(maxSharePixels / (dimensions.width * dimensions.height)));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(dimensions.width * scale));
  canvas.height = Math.max(1, Math.round(dimensions.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image sharing is unavailable in this browser.");

  const svgUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const image = await loadImage(svgUrl);
    context.fillStyle = "#0b0e09";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await canvasBlob(canvas);
    return new File([blob], filename, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

export type NativeShareResult = "shared" | "cancelled" | "unsupported";

function isWebKitBrowser() {
  const userAgent = navigator.userAgent;
  return /AppleWebKit/i.test(userAgent) && !/(CriOS|Chrome|Edg|FxiOS|Firefox|OPR)/i.test(userAgent);
}

export async function shareReplayCard({ file, title, text, url }: { file: File; title: string; text: string; url: string }): Promise<NativeShareResult> {
  if (typeof navigator.share !== "function") return "unsupported";
  const fileShare = { files: [file] };
  if (typeof navigator.canShare === "function" && !navigator.canShare(fileShare)) return "unsupported";
  try {
    // WebKit currently drops the file when text or a URL is included in the
    // same payload. The card already carries the site credit, so keep the
    // image as the only shared item on Safari and preserve the caption/link
    // payload for browsers that support both.
    await navigator.share(isWebKitBrowser() ? fileShare : { ...fileShare, title, text, url });
    return "shared";
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
    return "unsupported";
  }
}
